import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { Chart } from "primereact/chart";
import { Toast } from "primereact/toast";
import { useSettings } from "../context/SettingsContext";
import DashboardService from "../services/DashboardService";
import FeedBackService from "../services/FeedbackService";
import WorkTrackingDashboardWidget from "../components/WorkTracking/WorkTrackingDashboardWidget";

const feedbackTypes = [
  { label: "Öneri", value: "suggestion", icon: "pi pi-lightbulb", color: "text-amber-400" },
  { label: "Hata", value: "bug", icon: "pi pi-exclamation-triangle", color: "text-rose-400" },
  { label: "İstek", value: "request", icon: "pi pi-star", color: "text-indigo-400" }
];

const colorStyles = {
  indigo: { bg: "bg-indigo-500/10", text: "text-indigo-600 dark:text-indigo-400", border: "border-indigo-100 dark:border-indigo-950" },
  emerald: { bg: "bg-emerald-500/10", text: "text-emerald-600 dark:text-emerald-400", border: "border-emerald-100 dark:border-emerald-950" },
  blue: { bg: "bg-blue-500/10", text: "text-blue-600 dark:text-blue-400", border: "border-blue-100 dark:border-blue-950" },
  amber: { bg: "bg-amber-500/10", text: "text-amber-600 dark:text-amber-400", border: "border-amber-100 dark:border-amber-950" },
  rose: { bg: "bg-rose-500/10", text: "text-rose-600 dark:text-rose-400", border: "border-rose-100 dark:border-rose-950" }
};

function SaaSMetricCard({ title, value, subtitle, icon, color, hidden }) {
  const styles = colorStyles[color] || colorStyles.indigo;
  return (
    <div className="bg-white dark:bg-slate-800/90 p-4 rounded-xl shadow-sm border border-slate-200/80 dark:border-slate-800 flex flex-col justify-between h-full group hover:shadow-md transition-all duration-200 hover:-translate-y-0.5">
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="text-slate-500 dark:text-slate-400 text-[10px] font-black uppercase tracking-wider truncate">
          {title}
        </span>
        <span className={`flex !h-8 !w-8 shrink-0 aspect-square items-center justify-center rounded-lg ${styles.bg} ${styles.text}`}>
          <i className={`${icon} text-xs`} />
        </span>
      </div>
      <div>
        <h3 className="text-lg md:text-xl font-black text-slate-900 dark:text-white tracking-tight mb-0.5">
          {hidden ? "***" : value}
        </h3>
        {subtitle && (
          <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 flex items-center gap-1">
            <span className="h-1 w-1 rounded-full bg-slate-300 dark:bg-slate-600" />
            {hidden && subtitle.includes("₺") ? "Bugün: ***" : subtitle}
          </span>
        )}
      </div>
    </div>
  );
}

function FinanceOverviewCard({
  title,
  subtitle,
  icon,
  iconTone,
  glowTone,
  hidden,
  onToggle,
  chartData,
  chartOptions,
  metrics,
  formatCurrency,
}) {
  return (
    <section className="relative flex h-full min-h-[390px] flex-col overflow-hidden rounded-[1.75rem] border border-slate-200/80 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className={`pointer-events-none absolute -right-20 -top-24 h-56 w-56 rounded-full blur-3xl ${glowTone}`} aria-hidden="true" />

      <div className="relative flex items-start justify-between gap-3 px-5 pb-3 pt-5">
        <div className="flex min-w-0 items-center gap-3">
          <span className={`flex !h-11 !w-11 shrink-0 items-center justify-center rounded-2xl ring-4 ring-white/70 dark:ring-slate-950/30 ${iconTone}`}>
            <i className={`${icon} text-base`} />
          </span>
          <div className="min-w-0">
            <h2 className="m-0 text-base font-black tracking-tight text-slate-900 dark:text-white">{title}</h2>
            <p className="mb-0 mt-1 truncate text-[10px] font-semibold text-slate-500 dark:text-slate-400">{subtitle}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={onToggle}
          className="flex !h-9 !w-9 shrink-0 cursor-pointer appearance-none items-center justify-center rounded-xl border border-solid border-slate-200 bg-white/80 text-slate-500 [font-family:inherit] shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:border-slate-700 dark:bg-slate-800/80 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
          aria-label={hidden ? `${title} tutarlarını göster` : `${title} tutarlarını gizle`}
          title={hidden ? "Tutarları göster" : "Tutarları gizle"}
        >
          <i className={`pi ${hidden ? "pi-eye-slash" : "pi-eye"} text-sm`} />
        </button>
      </div>

      <div className="relative flex flex-1 flex-col px-5 pb-5">
        <div className="flex min-h-[132px] items-center justify-center">
          <div className="relative h-[118px] w-[118px]">
            <div className={`h-full w-full transition-all ${hidden ? "pointer-events-none opacity-20 blur-sm" : "opacity-100"}`}>
              <Chart type="doughnut" data={chartData} options={chartOptions} className="h-full w-full" />
            </div>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-[8px] font-black uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">{hidden ? "Gizli" : "Dağılım"}</span>
              <i className={`pi ${hidden ? "pi-eye-slash" : icon} mt-1 text-sm text-slate-500 dark:text-slate-400`} />
            </div>
          </div>
        </div>

        <div className="mt-auto overflow-hidden rounded-2xl border border-slate-200/80 bg-slate-50/70 dark:border-slate-800 dark:bg-slate-950/30">
          {metrics.map((metric, index) => (
            <div
              key={metric.label}
              className={`flex min-h-[49px] items-center justify-between gap-3 px-3.5 py-2.5 ${index < metrics.length - 1 ? "border-b border-slate-200/70 dark:border-slate-800" : ""}`}
            >
              <div className="flex min-w-0 items-center gap-2.5">
                <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${metric.dot}`} />
                <span className="truncate text-[10px] font-bold text-slate-600 dark:text-slate-300">{metric.label}</span>
              </div>
              <span className="shrink-0 text-[11px] font-black text-slate-900 dark:text-white">
                {hidden ? "••••" : formatCurrency(metric.value)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Dashboard() {
  const { settings } = useSettings();
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);
  const navigate = useNavigate();
  const toast = useRef(null);

  const [stats, setStats] = useState({
    todaySales: 0,
    monthSales: 0,
    todayCollections: 0,
    monthCollections: 0,
    todayExpenses: 0,
    monthExpenses: 0,
    todayDueReceivables: 0,
    monthDueReceivables: 0,
    monthPayables: 0,
    todayProfit: 0,
    monthProfit: 0,
    totalCustomers: 0,
    totalProducts: 0,
    inventoryCostValue: 0,
    inventorySaleValue: 0,
    monthlySalesTarget: 0,
    newCustomersCount: 0,
    averageOrderValue: 0,
    recentSales: [],
    criticalStockProducts: [],
    topSellingProducts: [],
    highImpactCustomers: [],
    salesChartData: [],
    upcomingExpenses: []
  });

  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState("");
  const [category, setCategory] = useState(feedbackTypes[0]);
  const [submitted, setSubmitted] = useState(false);
  const [hideKpis, setHideKpis] = useState(true);

  // Persistent privacy toggle (defaults to visible if not set)
  const [hideDaily, setHideDaily] = useState(() => {
    try {
      const saved = localStorage.getItem("dashboard_hide_daily");
      return saved !== null ? JSON.parse(saved) : false;
    } catch {
      return false;
    }
  });

  const [hideOutflow, setHideOutflow] = useState(() => {
    try {
      const saved = localStorage.getItem("dashboard_hide_outflow");
      return saved !== null ? JSON.parse(saved) : false;
    } catch {
      return false;
    }
  });

  const toggleHideDaily = () => {
    setHideDaily((prev) => {
      const next = !prev;
      try { localStorage.setItem("dashboard_hide_daily", JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const toggleHideOutflow = () => {
    setHideOutflow((prev) => {
      const next = !prev;
      try { localStorage.setItem("dashboard_hide_outflow", JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const maxLength = 500;

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const res = await DashboardService.getStats();
      const result = res.data;
      if (result && result.success && result.data) {
        setStats(result.data);
      }
    } catch (error) {
      console.error("Dashboard stats error:", error);
      if (toast.current) {
        toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Veriler yüklenirken bir sorun oluştu.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitFeedback = async () => {
    try {
      const res = await FeedBackService.sendFeedback({
        type: category?.value || "suggestion",
        message: feedback
      });
      const result = res.data || res;
      if (result.success === false) {
        toast.current.show({
          severity: "warn",
          summary: "Uyarı",
          detail: "Bildirim gönderilemedi." + (result.message ? " " + result.message : ""),
        }, 3000);
        return;
      } else {
        toast.current.show({
          severity: "success",
          summary: "Başarılı",
          detail: "Bildirim gönderildi. Teşekkürler!",
          life: 3000,
        });
        setSubmitted(true);
        setFeedback("");
      }
    } catch (error) {
      console.error(error);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(value || 0);
  };

  const salesTrendChartData = {
    labels: (stats.salesChartData || []).map(d => d.label),
    datasets: [
      {
        label: 'Satış Tutarı',
        data: (stats.salesChartData || []).map(d => d.value),
        fill: true,
        borderColor: '#10b981',
        backgroundColor: 'rgba(16, 185, 129, 0.08)',
        borderWidth: 3,
        pointBackgroundColor: '#fff',
        pointBorderColor: '#10b981',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 7,
        tension: 0.35
      }
    ]
  };

  const salesTrendChartOptions = {
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: settings.darkMode ? '#1e293b' : '#0f172a',
        padding: 12,
        titleFont: { size: 12, weight: '800' },
        bodyFont: { size: 12, weight: '600' },
        displayColors: false,
        callbacks: {
          label: (context) => `Satış: ${formatCurrency(context.raw)}`
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          color: settings.darkMode ? '#94a3b8' : '#64748b',
          font: { size: 10, weight: '700' }
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: settings.darkMode ? '#334155' : '#f1f5f9',
          borderDash: [5, 5]
        },
        ticks: {
          color: settings.darkMode ? '#94a3b8' : '#64748b',
          font: { size: 10, weight: '700' },
          callback: (value) => {
            if (value >= 1000) {
              return `${(value / 1000).toLocaleString('tr-TR')} bin ₺`;
            }
            return `${value} ₺`;
          }
        }
      }
    }
  };

  const doughnutData = {
    labels: (stats.topSellingProducts || []).slice(0, 4).map(p => p.productName),
    datasets: [
      {
        data: (stats.topSellingProducts || []).slice(0, 4).map(p => p.quantity),
        backgroundColor: [
          '#6366f1', // Indigo
          '#10b981', // Emerald
          '#3b82f6', // Blue
          '#f59e0b', // Amber
        ],
        borderWidth: 0
      }
    ]
  };

  const doughnutOptions = {
    maintainAspectRatio: false,
    cutout: '70%',
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: settings.darkMode ? '#94a3b8' : '#475569',
          font: { size: 10, weight: '700' },
          boxWidth: 8,
          padding: 8
        }
      },
      tooltip: {
        backgroundColor: settings.darkMode ? '#1e293b' : '#0f172a',
        padding: 10,
        titleFont: { size: 11, weight: '800' },
        bodyFont: { size: 11, weight: '600' },
        callbacks: {
          label: (context) => ` Satış: ${context.raw} Adet`
        }
      }
    }
  };

  const dailyFinanceDoughnutData = {
    labels: ['Günlük Gelir', 'Günlük Gider', 'Vadeli Alacak (Bugün)'],
    datasets: [
      {
        data: [stats.todayCollections, stats.todayExpenses, stats.todayDueReceivables],
        backgroundColor: ['#10b981', '#ef4444', '#6366f1'],
        hoverBackgroundColor: ['#059669', '#dc2626', '#4f46e5'],
        borderWidth: 0
      }
    ]
  };

  const dailyFinanceDoughnutOptions = {
    cutout: '75%',
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: settings.darkMode ? '#1e293b' : '#0f172a',
        padding: 10,
        callbacks: {
          label: (context) => ` Tutar: ${formatCurrency(context.raw)}`
        }
      }
    }
  };

  const outflowDoughnutData = {
    labels: ['Aylık Gider', 'Tedarikçi Borçları', 'Bekleyen Alacaklar'],
    datasets: [
      {
        data: [stats.monthExpenses, stats.monthPayables, stats.monthDueReceivables],
        backgroundColor: ['#ef4444', '#f59e0b', '#6366f1'],
        hoverBackgroundColor: ['#dc2626', '#d97706', '#4f46e5'],
        borderWidth: 0
      }
    ]
  };

  const outflowDoughnutOptions = {
    cutout: '75%',
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: settings.darkMode ? '#1e293b' : '#0f172a',
        padding: 10,
        callbacks: {
          label: (context) => ` Tutar: ${formatCurrency(context.raw)}`
        }
      }
    }
  };

  return (
    <div className="bg-[#f8fafc] dark:bg-slate-900 min-h-screen pb-24 font-inter">
      <Toast ref={toast} appendTo={mounted ? document.body : "self"} />

      <div className="max-w-[1600px] mx-auto px-4 md:px-6 space-y-4 md:space-y-5">

        {/* 1. COMPACT HEADER BANNER */}
        <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950 rounded-2xl px-4 py-3.5 sm:px-6 sm:py-4 text-white relative overflow-hidden shadow-lg border border-slate-800">
          <div className="absolute top-0 right-0 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl -mr-28 -mt-28 pointer-events-none" />
          <div className="absolute bottom-0 left-1/4 w-52 h-52 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />

          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-3.5">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-lg sm:text-xl md:text-2xl font-black text-white tracking-tight m-0">
                  İşletme Özeti
                </h1>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-400/20 text-[10px] font-black uppercase tracking-wider text-emerald-400">
                  <i className="pi pi-sparkles text-[9px]" />
                  Panel
                </span>
              </div>
              <p className="text-slate-300 text-xs sm:text-sm font-medium mt-0.5 m-0">
                Satış, tahsilat, müşteri ve stok durumunuzu anlık takip edin.
              </p>
            </div>

            {/* Quick Action Shortcuts */}
            <div className="flex flex-wrap items-center gap-2">
              <Button
                label="Hızlı Satış (F2)"
                icon="pi pi-shopping-cart"
                className="!bg-emerald-500 hover:!bg-emerald-600 !border-none !rounded-xl !font-black !px-3.5 !py-2 shadow-md shadow-emerald-500/20 transition-all active:scale-95 text-xs"
                onClick={() => navigate("/sales/productsale")}
              />
              <Button
                label="Stok Girişi"
                icon="pi pi-plus-circle"
                className="!bg-teal-600 hover:!bg-teal-700 !border-none !rounded-xl !font-black !px-3.5 !py-2 shadow-md shadow-teal-600/20 transition-all active:scale-95 text-xs"
                onClick={() => navigate("/stock/adjustment")}
              />
              <Button
                label="Müşteriler (F3)"
                icon="pi pi-users"
                className="!bg-indigo-600 hover:!bg-indigo-700 !border-none !rounded-xl !font-black !px-3.5 !py-2 shadow-md shadow-indigo-600/20 transition-all active:scale-95 text-xs"
                onClick={() => navigate("/customers")}
              />
              <Button
                label="Ürünler"
                icon="pi pi-box"
                className="!bg-amber-600 hover:!bg-amber-700 !border-none !rounded-xl !font-black !px-3.5 !py-2 shadow-md shadow-amber-600/20 transition-all active:scale-95 text-xs"
                onClick={() => navigate("/products")}
              />
              <Button
                label="Giderler"
                icon="pi pi-wallet"
                className="!bg-rose-600 hover:!bg-rose-700 !border-none !rounded-xl !font-black !px-3.5 !py-2 shadow-md shadow-rose-600/20 transition-all active:scale-95 text-xs"
                onClick={() => navigate("/expenses")}
              />
            </div>
          </div>
        </div>

        {/* 2. TOP KPI SUMMARY STRIP */}
        <div className="space-y-2">
          <div className="flex items-center justify-between px-0.5">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Finansal Göstergeler
              </span>
              {hideKpis && (
                <span className="text-[9px] font-black px-2 py-0.5 rounded-full bg-slate-200/80 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                  Gizli (***)
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => setHideKpis((prev) => !prev)}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-slate-200 bg-white/90 text-slate-600 text-xs font-bold shadow-xs transition-all hover:bg-slate-50 hover:text-slate-900 dark:border-slate-800 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 cursor-pointer"
              title={hideKpis ? "Değerleri Göster" : "Değerleri Gizle"}
            >
              <i className={`pi ${hideKpis ? "pi-eye" : "pi-eye-slash"} text-xs`} />
              <span className="text-[11px]">{hideKpis ? "Değerleri Göster" : "Değerleri Gizle"}</span>
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <SaaSMetricCard
              title="Bu Ayki Satış"
              value={formatCurrency(stats.monthSales)}
              subtitle={`Bugün: ${formatCurrency(stats.todaySales)}`}
              icon="pi pi-shopping-bag"
              color="indigo"
              hidden={hideKpis}
            />
            <SaaSMetricCard
              title="Bu Ayki Tahsilat"
              value={formatCurrency(stats.monthCollections)}
              subtitle={`Bugün: ${formatCurrency(stats.todayCollections)}`}
              icon="pi pi-wallet"
              color="emerald"
              hidden={hideKpis}
            />
            <SaaSMetricCard
              title="Bu Ayki Net Kar"
              value={formatCurrency(stats.monthProfit)}
              subtitle="Maliyet & giderler düşülmüş"
              icon="pi pi-chart-line"
              color="blue"
              hidden={hideKpis}
            />
            <SaaSMetricCard
              title="Kayıtlı Ürün Çeşidi"
              value={`${(stats.totalProducts || 0).toLocaleString("tr-TR")} Ürün`}
              subtitle="Aktif ürün envanteri"
              icon="pi pi-box"
              color="amber"
              hidden={hideKpis}
            />
          </div>
        </div>

        {/* 3. WORK TRACKING (FULL WIDTH EXPANDED ROW) */}
        <div>
          <WorkTrackingDashboardWidget />
        </div>

        {/* 4. FINANCIAL OVERVIEW CARDS (2 COLUMNS) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <FinanceOverviewCard
            title="Günlük Durum"
            subtitle="Bugünün gelir, gider ve vadeli işlemleri"
            icon="pi-chart-line"
            iconTone="bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-500/20"
            glowTone="bg-emerald-100/70 dark:bg-emerald-950/30"
            hidden={hideDaily}
            onToggle={toggleHideDaily}
            chartData={dailyFinanceDoughnutData}
            chartOptions={dailyFinanceDoughnutOptions}
            formatCurrency={formatCurrency}
            metrics={[
              { label: "Günlük Gelir", value: stats.todayCollections, dot: "bg-emerald-500" },
              { label: "Günlük Gider", value: stats.todayExpenses, dot: "bg-rose-500" },
              { label: "Vadeli Alacak (Bugün)", value: stats.todayDueReceivables, dot: "bg-indigo-500" },
            ]}
          />

          <FinanceOverviewCard
            title="Gider & Ödemeler"
            subtitle="Bu ayın giderleri ve vadeli ödemeleri"
            icon="pi-wallet"
            iconTone="bg-gradient-to-br from-rose-500 to-orange-500 text-white shadow-lg shadow-rose-500/20"
            glowTone="bg-rose-100/70 dark:bg-rose-950/30"
            hidden={hideOutflow}
            onToggle={toggleHideOutflow}
            chartData={outflowDoughnutData}
            chartOptions={outflowDoughnutOptions}
            formatCurrency={formatCurrency}
            metrics={[
              { label: "Aylık Gider", value: stats.monthExpenses, dot: "bg-rose-500" },
              { label: "Tedarikçi Borçları", value: stats.monthPayables, dot: "bg-amber-500" },
              { label: "Bekleyen Alacaklar", value: stats.monthDueReceivables, dot: "bg-indigo-500" },
            ]}
          />
        </div>

        {/* 4. SALES TREND & TOP SELLING PRODUCTS ROW */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Satış Trendi Çizgi Grafiği */}
          <div
            className="lg:col-span-8 p-5 md:p-6 rounded-2xl shadow-sm border flex flex-col justify-between min-h-[340px]"
            style={{
              backgroundColor: settings.darkMode ? '#1e293b' : '#ffffff',
              borderColor: settings.darkMode ? '#334155' : '#f1f5f9'
            }}
          >
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-black text-slate-900 dark:text-white m-0">Satış Trendi</h3>
                <p className="text-xs text-slate-400 font-bold m-0 mt-0.5">Son günlerin günlük satış hacmi performansı</p>
              </div>
              <span className="px-3 py-1 rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400 text-xs font-black">
                {stats.monthSales > 0 ? `Toplam: ${formatCurrency(stats.monthSales)}` : "Canlı Akış"}
              </span>
            </div>

            <div className="h-[240px] w-full relative">
              {(stats.salesChartData || []).length > 0 ? (
                <Chart type="line" data={salesTrendChartData} options={salesTrendChartOptions} className="w-full h-full" />
              ) : (
                <div className="h-full w-full flex items-center justify-center text-slate-400 text-xs font-bold italic">
                  Henüz satış trend verisi bulunmuyor.
                </div>
              )}
            </div>
          </div>

          {/* En Çok Satan Ürünler */}
          <div
            className="lg:col-span-4 p-5 md:p-6 rounded-2xl shadow-sm border flex flex-col justify-between min-h-[340px]"
            style={{
              backgroundColor: settings.darkMode ? '#1e293b' : '#ffffff',
              borderColor: settings.darkMode ? '#334155' : '#f1f5f9'
            }}
          >
            <div className="mb-2.5">
              <h3 className="text-lg font-black text-slate-900 dark:text-white m-0">En Çok Satanlar</h3>
              <p className="text-xs text-slate-400 font-bold m-0 mt-0.5">Bu ayın en popüler ürünleri</p>
            </div>
            <div className="h-[175px] w-full flex items-center justify-center relative my-auto">
              {(stats.topSellingProducts || []).length > 0 ? (
                <Chart type="doughnut" data={doughnutData} options={doughnutOptions} className="w-full h-full" />
              ) : (
                <span className="text-slate-400 text-xs font-bold italic">Satış bulunmuyor.</span>
              )}
            </div>
            <div className="mt-3 flex items-center justify-between text-xs font-bold text-slate-500 border-t border-slate-100 dark:border-slate-700/50 pt-2.5">
              <span>Toplam Satılan:</span>
              <span className="text-slate-800 dark:text-slate-200 font-black">
                {stats.topSellingProducts.reduce((sum, p) => sum + p.quantity, 0)} Adet
              </span>
            </div>
          </div>
        </div>

        {/* 5. RECENT SALES & CRITICAL STOCK ROW */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6">
          {/* Son Satış İşlemleri */}
          <div
            className="lg:col-span-7 p-5 rounded-2xl shadow-sm border flex flex-col justify-between min-h-[300px]"
            style={{
              backgroundColor: settings.darkMode ? '#1e293b' : '#ffffff',
              borderColor: settings.darkMode ? '#334155' : '#f1f5f9'
            }}
          >
            <div className="mb-3 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-black text-slate-900 dark:text-white m-0">Son Satışlar</h3>
                <p className="text-xs text-slate-400 font-bold m-0 mt-0.5">Sisteme kaydedilen son işlemler</p>
              </div>
              <Button
                label="Tümü"
                icon="pi pi-arrow-right"
                iconPos="right"
                className="p-button-text p-button-sm font-black p-0 text-indigo-600 dark:text-indigo-400"
                onClick={() => navigate("/sales")}
              />
            </div>
            <div className="overflow-x-auto my-auto">
              <table className="w-full border-collapse text-left text-xs">
                <thead>
                  <tr className="border-b border-slate-100 dark:border-slate-700/50 text-slate-400 font-bold">
                    <th className="py-2">Tarih</th>
                    <th className="py-2">Müşteri</th>
                    <th className="py-2 text-right">Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {(stats.recentSales || []).length > 0 ? (
                    stats.recentSales.slice(0, 5).map((sale, i) => (
                      <tr key={i} className="border-b border-slate-50 dark:border-slate-800/40 last:border-none text-slate-700 dark:text-slate-300 font-semibold">
                        <td className="py-2.5">{new Date(sale.saleDate).toLocaleDateString("tr-TR")}</td>
                        <td className="py-2.5 truncate max-w-[140px]">
                          {sale.customerId > 0 ? (
                            <span
                              onClick={() => navigate(`/customers/detail/${sale.customerId}`)}
                              className="font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 hover:underline cursor-pointer transition-all"
                            >
                              {sale.customerName}
                            </span>
                          ) : (
                            <span className="font-bold text-slate-900 dark:text-white">
                              {sale.customerName}
                            </span>
                          )}
                        </td>
                        <td className="py-2.5 text-right">
                          <span className={`px-2.5 py-1 rounded-full text-[10px] font-black ${(sale.customerId <= 0 || sale.balanceTotal <= 0)
                              ? "bg-emerald-50 text-emerald-600 dark:bg-emerald-950/30 dark:text-emerald-400"
                              : sale.paidTotal > 0
                                ? "bg-amber-50 text-amber-600 dark:bg-amber-950/30 dark:text-amber-400"
                                : "bg-rose-50 text-rose-600 dark:bg-rose-950/30 dark:text-rose-400"
                            }`}>
                            {sale.customerId <= 0 ? "TAHSİL EDİLDİ" : sale.balanceTotal <= 0 ? "ÖDENDİ" : sale.paidTotal > 0 ? "KISMİ" : "BEKLİYOR"}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" className="py-6 text-center text-slate-400 italic">Satış bulunmuyor.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Stok & Envanter Durumu */}
          <div
            className="lg:col-span-5 p-5 rounded-2xl shadow-sm border flex flex-col justify-between min-h-[300px]"
            style={{
              backgroundColor: settings.darkMode ? '#1e293b' : '#ffffff',
              borderColor: settings.darkMode ? '#334155' : '#f1f5f9'
            }}
          >
            <div className="mb-3 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-black text-slate-900 dark:text-white m-0">Kritik Stok</h3>
                <p className="text-xs text-slate-400 font-bold m-0 mt-0.5">Tükenmek üzere olan ürünler</p>
              </div>
              <Button
                label="Tümü"
                icon="pi pi-arrow-right"
                iconPos="right"
                className="p-button-text p-button-sm font-black p-0 text-indigo-600 dark:text-indigo-400"
                onClick={() => navigate("/stock/critical")}
              />
            </div>
            <div className="flex-1 flex flex-col justify-center gap-2.5">
              {(stats.criticalStockProducts || []).length > 0 ? (
                (stats.criticalStockProducts || []).slice(0, 4).map((p, i) => (
                  <div key={i} className="flex justify-between items-center border-b border-slate-50 dark:border-slate-800/40 pb-2 last:border-none last:pb-0">
                    <span className="font-bold text-slate-700 dark:text-slate-300 truncate max-w-[200px]">{p.productName}</span>
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-black bg-rose-50 text-rose-600 dark:bg-rose-950/40 dark:text-rose-400">
                      {p.stockQuantity} {p.unit || 'Adet'}
                    </span>
                  </div>
                ))
              ) : (
                <div className="flex flex-col items-center justify-center py-4 text-center">
                  <i className="pi pi-check-circle text-emerald-500 text-3xl mb-2" />
                  <span className="text-slate-600 dark:text-slate-300 text-xs font-bold">Tüm stoklar normal seviyede!</span>
                </div>
              )}
            </div>
            <div className="mt-3 flex items-center justify-between text-xs font-bold text-slate-500 border-t border-slate-100 dark:border-slate-700/50 pt-2.5">
              <span>Toplam Ürün Çeşidi:</span>
              <span className="text-slate-800 dark:text-slate-200 font-black">
                {stats.totalProducts} Ürün
              </span>
            </div>
          </div>
        </div>

        {/* 6. HELP & FEEDBACK HUB */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* Left Column: Help Center & Shortcuts Banner */}
          <div className="lg:col-span-5 bg-gradient-to-br from-indigo-600 via-indigo-700 to-blue-700 text-white rounded-3xl p-6 shadow-md shadow-indigo-600/20 relative overflow-hidden flex flex-col justify-between min-h-[200px]">
            <div className="absolute top-0 right-0 -mr-6 -mt-6 w-36 h-36 bg-white/10 rounded-full blur-2xl pointer-events-none" />
            <div className="absolute bottom-0 right-4 opacity-10 pointer-events-none">
              <i className="pi pi-question-circle text-8xl" />
            </div>

            <div className="relative z-10">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/20 backdrop-blur-md text-[11px] font-black uppercase tracking-wider mb-2.5">
                <i className="pi pi-compass text-xs" />
                <span>Yardım & Destek</span>
              </div>
              <h3 className="text-lg lg:text-xl font-black text-white m-0 tracking-tight leading-snug">
                Yardıma mı İhtiyacınız Var?
              </h3>
              <p className="text-xs text-white/85 font-medium mt-1 leading-relaxed max-w-sm">
                Satış, Tahsilat, Maliyetler, Stok ve Kasa rehberine, kısayollara ve ipuçlarına anında ulaşın.
              </p>
            </div>

            <div className="relative z-10 flex items-center gap-2.5 flex-wrap mt-4">
              <button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent("open-help-center", { detail: { tab: "faq" } }))}
                style={{ backgroundColor: "#ffffff", color: "#4338ca" }}
                className="hover:!bg-slate-100 font-black text-xs px-4 py-2.5 rounded-xl shadow-md flex items-center gap-2 transition-all cursor-pointer border-none"
              >
                <i className="pi pi-question-circle text-sm" style={{ color: "#4338ca" }} />
                <span style={{ color: "#4338ca" }}>Yardım Merkezini Aç</span>
              </button>

              <button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent("open-help-center", { detail: { tab: "shortcuts" } }))}
                className="bg-white/20 hover:bg-white/30 text-white font-black text-xs px-3.5 py-2.5 rounded-xl border border-white/30 backdrop-blur-sm flex items-center gap-1.5 transition-all cursor-pointer"
              >
                <i className="pi pi-bolt text-xs text-amber-300" />
                <span>Kısayollar</span>
              </button>
            </div>
          </div>

          {/* Right Column: Quick Feedback Form */}
          <div className="lg:col-span-7 bg-white dark:bg-slate-850/90 rounded-3xl p-6 shadow-sm border border-slate-200/80 dark:border-slate-800 flex flex-col justify-between">
            <div className="mb-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h3 className="text-base font-black text-slate-900 dark:text-white m-0">
                  Geri Bildirim & Öneri İletin
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 font-medium mt-0.5 m-0">
                  KasamBenim'i geliştirmemize yardımcı olun; öneri veya hataları iletin.
                </p>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs text-slate-500 font-bold">Kategori:</span>
                <Dropdown
                  value={category}
                  options={feedbackTypes}
                  optionLabel="label"
                  onChange={(e) => setCategory(e.value)}
                  className="w-[140px] rounded-xl !bg-slate-100 dark:!bg-slate-800 !border-slate-200 dark:!border-slate-700 !text-xs !font-bold"
                  valueTemplate={(option, props) => {
                    const selected = option || props.value || feedbackTypes[0];
                    return (
                      <div className="flex items-center gap-1.5 py-0.5">
                        <i className={`${selected.icon} ${selected.color} text-xs`} />
                        <span className="font-black text-xs text-slate-800 dark:text-slate-200">
                          {selected.label}
                        </span>
                      </div>
                    );
                  }}
                  itemTemplate={(option) => option && (
                    <div className="flex items-center gap-2 py-0.5">
                      <i className={`${option.icon} ${option.color} text-xs`} />
                      <span className="font-bold text-xs text-slate-700 dark:text-slate-300">{option.label}</span>
                    </div>
                  )}
                />
              </div>
            </div>

            {!submitted ? (
              <div className="flex flex-col gap-3">
                <InputTextarea
                  value={feedback}
                  onChange={(e) => e.target.value.length <= maxLength && setFeedback(e.target.value)}
                  rows={2}
                  placeholder="Görüşünüzü, önerinizi veya karşılaştığınız bir durumu yazın..."
                  className="w-full rounded-2xl p-3 text-xs bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700 text-slate-850 dark:text-white font-medium focus:ring-2 focus:ring-indigo-500 resize-none"
                />
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-slate-400 font-bold">
                    {feedback.length}/{maxLength}
                  </span>
                  <Button
                    label="Bildirimi İlet"
                    icon="pi pi-send"
                    className="!bg-indigo-600 hover:!bg-indigo-500 !border-none rounded-xl font-black text-xs py-2 px-4 text-white shadow-md shadow-indigo-600/30 cursor-pointer"
                    disabled={!feedback.trim()}
                    onClick={handleSubmitFeedback}
                  />
                </div>
              </div>
            ) : (
              <div className="py-4 flex flex-col items-center text-center gap-2">
                <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-950/60 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                  <i className="pi pi-check-circle text-xl" />
                </div>
                <div className="text-xs font-black text-slate-800 dark:text-white">
                  Geri bildiriminiz başarıyla iletildi. Teşekkürler!
                </div>
                <Button
                  label="Yeni Bildirim Yaz"
                  className="p-button-text p-button-sm text-indigo-600 dark:text-indigo-400 font-bold"
                  onClick={() => setSubmitted(false)}
                />
              </div>
            )}
          </div>
        </div>

      </div>

      <style>{`
        .p-datatable.no-header .p-datatable-thead { display: none !important; }
        .p-datatable.no-header .p-datatable-tbody > tr { border-bottom: 1px solid #f8fafc !important; }
        .p-datatable.no-header .p-datatable-tbody > tr:last-child { border: none !important; }
      `}</style>
    </div>
  );
}
